import React, { useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * RoleTierModal — promote / demote a user along the in-org role tier.
 *
 * Tiers (ascending):
 *   • staff
 *   • driver
 *   • room_leader        (needs `assigned_room_id`; scoped to one room)
 *   • deputy_manager     (admin minus billing / branding / admin-mgmt)
 *   • org_admin          (full org admin — owner-only assignment)
 *
 * Props:
 *   open         : boolean
 *   user         : current user object (target of the change)
 *   currentUser  : the logged-in admin (used to gate which roles they
 *                  can grant — a deputy can't appear here at all)
 *   rooms        : list of rooms in the org (for room_leader picker)
 *   onClose      : () => void
 *   onSaved      : () => void  - parent should reload after save
 */
export function RoleTierModal({ open, user, currentUser, rooms = [], onClose, onSaved }) {
  const [role, setRole] = useState(user?.role || 'staff');
  const [assignedRoomId, setAssignedRoomId] = useState(user?.assigned_room_id || '');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset state whenever the modal re-opens for a different user.
  React.useEffect(() => {
    if (open && user) {
      setRole(user.role || 'staff');
      setAssignedRoomId(user.assigned_room_id || '');
      setReason('');
      setError(null);
    }
  }, [open, user]);

  if (!open || !user) return null;

  // Role grant matrix — what the *current* admin is allowed to assign.
  const isOwner = currentUser?.role === 'org_owner' || currentUser?.role === 'super_admin';
  // org_admin can grant deputy/room_leader/staff/driver. Only org_owner
  // can grant org_admin (creating another full admin).
  const ROLE_OPTIONS = [
    { value: 'staff', label: 'Staff', desc: 'Standard staff member — rota, timesheets, learning reports.' },
    { value: 'driver', label: 'Driver', desc: 'Transport routes & manifests only.' },
    { value: 'room_leader', label: 'Room Leader', desc: 'Staff + scoped admin powers for ONE assigned room (approve room timesheets, edit attendance/incidents for that room).' },
    { value: 'deputy_manager', label: 'Deputy Manager', desc: 'Admin powers EXCEPT billing, branding, suspending the org, or promoting other admins.' },
    {
      value: 'org_admin',
      label: 'Org Admin',
      desc: 'Full organisation admin. Only the org owner can grant this role.',
      ownerOnly: true,
    },
  ];

  const targetIsOwner = user.role === 'org_owner';
  const isSelf = currentUser?._id === user._id;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = { role, reason: reason.trim() || undefined };
      if (role === 'room_leader') {
        if (!assignedRoomId) {
          throw new Error('Please pick a room to assign this leader to.');
        }
        body.assigned_room_id = assignedRoomId;
      } else {
        body.assigned_room_id = null;
      }
      await api.changeUserRole(user._id, body);
      toast.success(`${user.full_name} is now ${ROLE_OPTIONS.find(r => r.value === role)?.label || role}`, {
        description: role === 'room_leader' ? `Scoped to ${rooms.find(r => r._id === assignedRoomId)?.name || 'their assigned room'}.` : undefined,
        duration: 5000,
      });
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      data-testid="role-tier-modal"
    >
      <div
        className="modal-content"
        style={{ maxWidth: 580, padding: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid hsl(var(--border))' }}>
          <h2 style={{ margin: 0 }}>Change role: {user.full_name}</h2>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
            Currently: <strong>{user.role}</strong>
            {user.assigned_room_id && rooms.find(r => r._id === user.assigned_room_id) ? (
              <> — assigned to <strong>{rooms.find(r => r._id === user.assigned_room_id).name}</strong></>
            ) : null}
          </p>
        </div>

        <div style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {targetIsOwner && (
            <div style={{ background: 'hsl(0 90% 95%)', border: '1px solid hsl(0 80% 80%)', padding: '0.65rem 0.85rem', borderRadius: 6, color: 'hsl(0 80% 30%)', fontSize: '0.85rem' }}>
              The organisation owner's role cannot be changed from this screen. Use the dedicated owner-transfer flow if you need to hand over the org.
            </div>
          )}
          {isSelf && (
            <div style={{ background: 'hsl(45 95% 92%)', border: '1px solid hsl(45 85% 70%)', padding: '0.65rem 0.85rem', borderRadius: 6, fontSize: '0.85rem' }}>
              You can't change your own role — ask another admin if you need a different tier.
            </div>
          )}

          {error && (
            <div className="error-message" data-testid="role-tier-error">{error}</div>
          )}

          <fieldset disabled={targetIsOwner || isSelf || saving} style={{ border: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {ROLE_OPTIONS.map((opt) => {
              const disabled = opt.ownerOnly && !isOwner;
              const checked = role === opt.value;
              return (
                <label
                  key={opt.value}
                  data-testid={`role-tier-option-${opt.value}`}
                  style={{
                    display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
                    padding: '0.7rem 0.85rem',
                    borderRadius: 6,
                    border: `1px solid ${checked ? 'hsl(var(--primary))' : 'hsl(var(--border))'}`,
                    background: checked ? 'hsl(var(--primary)/0.07)' : 'hsl(var(--background))',
                    opacity: disabled ? 0.5 : 1,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="role-tier"
                    value={opt.value}
                    checked={checked}
                    onChange={() => setRole(opt.value)}
                    disabled={disabled}
                    style={{ marginTop: 3 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>
                      {opt.label}
                      {opt.ownerOnly && (
                        <span style={{ marginLeft: 6, fontSize: '0.65rem', padding: '0.05rem 0.4rem', borderRadius: 999, background: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}>
                          Owner-only
                        </span>
                      )}
                    </div>
                    <small style={{ color: 'hsl(var(--muted-foreground))' }}>{opt.desc}</small>
                  </div>
                </label>
              );
            })}
          </fieldset>

          {role === 'room_leader' && (
            <div className="form-group" style={{ margin: 0 }}>
              <label>Assigned room *</label>
              <select
                value={assignedRoomId}
                onChange={(e) => setAssignedRoomId(e.target.value)}
                data-testid="role-tier-assigned-room"
                disabled={saving}
              >
                <option value="">— Pick a room —</option>
                {rooms.map((r) => (
                  <option key={r._id} value={r._id}>{r.name} {r.age_group ? `(${r.age_group})` : ''}</option>
                ))}
              </select>
              <small style={{ color: 'hsl(var(--muted-foreground))', display: 'block', marginTop: 4 }}>
                Room Leaders can approve their room's timesheets and edit attendance / incidents for the children in that room.
              </small>
            </div>
          )}

          <div className="form-group" style={{ margin: 0 }}>
            <label>Reason (optional)</label>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Documented in the audit log."
              data-testid="role-tier-reason"
              disabled={saving || targetIsOwner || isSelf}
            />
          </div>
        </div>

        <div style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid hsl(var(--border))', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving} data-testid="role-tier-cancel">
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || targetIsOwner || isSelf || role === user.role && (assignedRoomId || '') === (user.assigned_room_id || '')}
            data-testid="role-tier-save"
          >
            {saving ? 'Saving…' : 'Save role'}
          </button>
        </div>
      </div>
    </div>
  );
}
